import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ComponentSync, EditableDBItemV3, getElementCenterPos, Grid, LL_H_C, LL_V_L, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, TS_Input, TS_PropRenderer, TS_Radio } from '@nu-art/thunderstorm-frontend';
import { ModuleFE_DiseaseValue, ModuleFE_MultiVar } from '../../../../../_entity.js';
import './Editor_DiseaseValue.scss';
import { asArray, exists, MUSTNeverHappenException, voidFunction } from '@nu-art/ts-common';
import { ConfigKeyFE_ModalityTagId, DropDown_Tag, DropDown_Values, ModuleFE_Expression, ModuleFE_Value, ModuleFE_Variable } from '@app/hcs-frontend';
import { DropDown_Likelihood, DropDown_LikelihoodDirection, DropDown_Severity } from '../../../../../_enum.js';
import { Toaster } from '@nu-art/toasting';
import { Dialog_Confirmation, ICONS } from '@app/styles';
import { AccessLevel } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { Component_ExpressionPrettyPrinter } from '@app/hcs-frontend/ui/expressions/ui/expression-pretty-print/Component_ExpressionPrettyPrinter';
import { Dialog_AssignDPVExpression } from '../Dialog_AssignDPVExpression/Dialog_AssignDPVExpression.js';
import { Window_ExpressionEditor } from '@app/hcs-frontend/ui/_editors/expression/Window_ExpressionEditor/Window_ExpressionEditor';
export class Editor_DiseaseValue extends ComponentSync {
    toaster = new Toaster('disease-value');
    // ######################## Life Cycle ########################
    __onExpressionUpdated = (...params) => {
        const expressionId = this.state.editable.item.expressionId;
        if (exists(expressionId) && asArray(params[1]).some(exp => exp._id === expressionId))
            this.forceUpdate();
    };
    deriveStateFromProps(nextProps, state) {
        state.editable = this.getEditable(nextProps.diseaseValue);
        state.editMode = nextProps.editMode;
        state.showSourceEditor = !!nextProps.showSourceEditor;
        return state;
    }
    // ######################## Logic ########################
    getEditable(diseaseValue) {
        return new EditableDBItemV3(diseaseValue, ModuleFE_DiseaseValue)
            .setAutoSave(true)
            .setOnChanged(async () => this.forceUpdate())
            .setOnSaveCompleted(() => this.forceUpdate())
            .setDebounceTimeout(200);
    }
    getSortedVariableIds = () => {
        const multiVar = ModuleFE_MultiVar.cache.unique(this.state.editable.item.multiVarId);
        if (!multiVar)
            throw new MUSTNeverHappenException(`Could not find multi-var for id ${this.state.editable.item.multiVarId}`);
        return multiVar.variables.map(mvv => mvv.variableId);
    };
    parse_rangeToStr = (range) => {
        if (!exists(range) || !exists(range[0]) || !exists(range[1]))
            return;
        if (range[0] === range[1])
            return `${range[0]}`;
        return `${range[0]} - ${range[1]}`;
    };
    parse_strToRange = (str) => {
        if (!str?.trim())
            return;
        const components = str.split('-').map(str => Number(str.trim()));
        if (components.length === 1 && !isNaN(components[0]))
            return [components[0], components[0]];
        if (components.length !== 2 || components.some(num => isNaN(num)))
            return this.toaster.toastError({
                title: 'Parsing Error',
                body: `string: "${str}" could no be parsed into a valid range`,
            });
        if (components[0] > components[1])
            return this.toaster.toastError({
                title: 'Logical Error',
                body: `maximum in range [${components[0]},${components[1]}] should be higher than the minimum`,
            });
        return components;
    };
    onDeleteDiseaseValue = () => {
        ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
        Dialog_Confirmation.show({
            header: 'Delete Disease Value',
            content: () => 'Delete this disease value',
            buttons: [{
                    label: 'Delete',
                    isAsync: true,
                    action: async () => {
                        try {
                            await this.state.editable.delete();
                            this.toaster.toastSuccess({ title: 'Disease Value Delete', body: 'Disease value deleted successfully' });
                        }
                        catch (err) {
                            this.logError('WorkHubItem_DiseaseProfile', err);
                            this.toaster.toastError({ title: 'Disease Value Delete', body: `Disease value deletion failed:\n${err.message}` });
                        }
                    }
                },
                {
                    label: 'Cancel',
                    isPreferred: true,
                    action: voidFunction
                }]
        });
    };
    updateEditable = (dv) => this.setState({ editable: this.getEditable(dv) });
    openMenu = (e) => {
        const dv = this.state.editable.item;
        const model = {
            id: 'disease-value-menu',
            originPos: getElementCenterPos(e.currentTarget),
            modalPos: { x: -1, y: 1 },
            xAxisAnchor: 'right',
            offset: { x: 8 },
            content: () => _jsxs(_Fragment, { children: [exists(dv._id) && _jsx(Button, { onClick: this.onDeleteDiseaseValue, children: "Delete" }), exists(dv._id) && !exists(dv.expressionId) &&
                        _jsx(Button, { onClick: async () => {
                                const response = await ModuleFE_DiseaseValue._v1.convertToExpression({ id: dv._id }).executeSync();
                                this.updateEditable(response.diseaseValue);
                                ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                            }, children: "Convert to expression" }), _jsx(Button, { onClick: () => {
                            Dialog_AssignDPVExpression.show({ diseaseValue: dv, onSave: this.updateEditable });
                            ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                        }, children: "Assign Existing Expression" })] }),
        };
        ModuleFE_MouseInteractivity.showContent(model);
    };
    openExpression = () => {
        const expressionId = this.state.editable.item.expressionId;
        if (!expressionId)
            return;
        const canEdit = ModuleFE_Expression.permissions.collection.edit.getAccessLevel() === AccessLevel.HasAccess;
        Window_ExpressionEditor.show({
            expressionId,
            editMode: canEdit,
            editorConfig: {
                label: true,
                terms: true,
                outputType: false,
                output: true,
            }
        });
    };
    // ######################## Render ########################
    render() {
        return _jsxs(LL_V_L, { className: 'e__disease-value', children: [this.render_DynamicContent(), this.render_Properties()] });
    }
    render_DynamicContent = () => {
        if (!exists(this.state.editable.item.expressionId))
            return this.render_Variables();
        return this.render_Expression();
    };
    // ######################## Render - Expression ########################
    render_Expression = () => {
        const expression = ModuleFE_Expression.cache.unique(this.state.editable.item.expressionId);
        const editable = new EditableDBItemV3(expression ?? {}, ModuleFE_Expression);
        return _jsxs(LL_H_C, { className: 'e__disease-value__expression', children: [_jsx(ICONS.expressions.component, { className: 'expression-mark', onClick: this.openExpression }), _jsx(Component_ExpressionPrettyPrinter, { editable: editable, variablesModule: ModuleFE_Variable, valueModule: ModuleFE_Value, expressionsModule: ModuleFE_Expression }), this.state.editMode && _jsx(ICONS.more.component, { className: 'menu-icon', onClick: this.openMenu })] });
    };
    // ######################## Render - Variables ########################
    render_Variables = () => {
        const variableIds = this.getSortedVariableIds();
        const editable = this.state.editable.editProp('values', {});
        return _jsx(Grid, { className: 'e__disease-value__variables', children: variableIds.map((id, index) => this.render_Variable(editable, id, index)) });
    };
    render_Variable = (editable, varId, index) => {
        const variable = ModuleFE_Variable.cache.unique(varId);
        const selectedValueId = editable.item[varId];
        //No need to show row if there is no value & we are not in edit mode
        if (!this.state.editMode && !selectedValueId)
            return;
        const label = (variable?.name ?? 'N/A') + ' :';
        return _jsxs(TS_PropRenderer.Horizontal, { className: 'disease-value-row', label: label, children: [!!variable && _jsx(DropDown_Values, { canUnselect: true, disabled: !this.state.editMode, variable: variable, selected: selectedValueId, onSelected: value => {
                        editable.updateObj({ [varId]: value?._id });
                    } }), !index && this.state.editMode && _jsx(ICONS.more.component, { className: 'menu-icon', onClick: this.openMenu })] }, varId);
    };
    // ######################## Render - Properties ########################
    render_Properties = () => {
        return _jsxs(Grid, { className: 'e__disease-value__properties', children: [this.render_Source(), this.render_Indicative(), this.render_Likelihood(), this.render_Severity()] });
    };
    render_Indicative = () => {
        const editable = this.state.editable;
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Indicative:', className: 'disease-value-row', children: _jsx(TS_Radio, { checked: editable.item.indicative, onCheck: value => editable.updateObj({ indicative: value }), groupName: 'indicative', disabled: !this.state.editMode, values: [undefined, 1, -1], labelRenderer: value => {
                    switch (value) {
                        case undefined:
                            return this.state.editMode ? 'Unselected' : '–';
                        case 1:
                            return 'Rule In';
                        case -1:
                            return 'Rule Out';
                    }
                } }) });
    };
    render_Likelihood = () => {
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Likelihood:', className: 'disease-value-row likelihood', children: _jsxs(LL_V_L, { children: [this.render_Likelihood_Range(), _jsxs(LL_H_C, { children: [this.render_Likelihood_Descriptive(), this.render_Likelihood_Direction()] })] }) });
    };
    render_Likelihood_Range = () => {
        const editable = this.state.editable;
        const currentValue = editable.item.likelihoodRange;
        const isSingleValue = currentValue !== undefined && currentValue[0] === currentValue[1];
        const baseStr = this.parse_rangeToStr(currentValue);
        const displayValue = isSingleValue && !this.state.editMode ? `${baseStr} %` : baseStr;
        return _jsx(LL_H_C, { className: 'e__disease-value__likelihood-range', children: _jsx(TS_Input, { type: 'text', value: displayValue, placeholder: this.state.editMode ? 'LIKELIHOOD RANGE' : '–', disabled: !this.state.editMode, onBlur: value => {
                    const nextRange = this.parse_strToRange(value);
                    editable.updateObj({ likelihoodRange: nextRange ?? undefined });
                } }) });
    };
    render_Likelihood_Descriptive = () => {
        return _jsx(DropDown_Likelihood.editable, { editable: this.state.editable, prop: 'likelihood', disabled: !this.state.editMode, placeholder: this.state.editMode ? 'Unselected' : '–', canUnselect: true });
    };
    render_Likelihood_Direction = () => {
        return _jsx(DropDown_LikelihoodDirection.editable, { editable: this.state.editable, prop: 'likelihoodDirection', disabled: !this.state.editMode, placeholder: this.state.editMode ? 'Unselected' : '–', canUnselect: true });
    };
    render_Severity = () => {
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Severity:', className: 'disease-value-row', children: _jsx(DropDown_Severity.editable, { editable: this.state.editable, prop: 'severityFactor', disabled: !this.state.editMode, placeholder: this.state.editMode ? 'Unselected' : '–', canUnselect: true }) });
    };
    render_Source = () => {
        if (!this.state.showSourceEditor)
            return;
        const modalityTagId = ConfigKeyFE_ModalityTagId.get();
        return _jsx(TS_PropRenderer.Horizontal, { label: 'Source:', className: 'disease-value-row', children: _jsx(DropDown_Tag.editable, { editable: this.state.editable, prop: 'sourceId', disabled: !this.state.editMode, queryFilter: tag => tag.tagIds.includes(modalityTagId), placeholder: this.state.editMode ? 'Unselected' : '–', canUnselect: true }) });
    };
}
//# sourceMappingURL=Editor_DiseaseValue.js.map